import {
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeEvalOffset,
  polyLine2Length,
  polyLine2RotateDegrees,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";

/** Everything needed to place the pivot a rotated cabinet turns about — see {@link rotateCaseworkHostPath}. */
export interface CaseworkHostRotation {
  /** The doc's `rotation`: degrees clockwise in plan, measured in unmirrored doc space. */
  readonly rotationDegrees: number;
  /**
   * Distance along the host path to the point the cabinet is pinned to the wall at — the placement
   * param *before* horizontal alignment slides the box off it, so the two compose independently.
   */
  readonly anchorParam: number;
  /**
   * Perpendicular displacement from the host path out to that same anchor: the negated lateral
   * offset *before* lateral alignment slides the box off it, so the two halves of the anchor are
   * captured the same way. Which face of the box then lands on it is the alignment's business.
   */
  readonly anchorOffset: number;
  /** Whether the placement is anchored from the far end of the host path, reversing `anchorParam`. */
  readonly paramFromEnd: boolean;
  /** Whether the plan is mirrored, which reverses `anchorParam` and the direction of the turn. */
  readonly mirror: boolean;
}

/**
 * The cabinet's host path, yawed about its placement anchor. Every layer, door, filler, plan loop,
 * tag and local-to-world transform is derived from this path, so turning it turns all of them.
 *
 * A rigid turn preserves arc length, so the box keeps its params along the path: the anchor stays
 * pinned to the wall and the cabinet swings away from it. 0° returns `hostPath` itself, untouched.
 */
export function rotateCaseworkHostPath(
  hostPath: PolyLine2,
  { rotationDegrees, anchorParam, anchorOffset, paramFromEnd, mirror }: CaseworkHostRotation,
): PolyLine2 {
  if (rotationDegrees === 0 || polyLine2EdgeCount(hostPath) < 1) {
    return hostPath;
  }

  const [anchorX, anchorY] = caseworkAnchorPoint(hostPath, anchorParam, anchorOffset, paramFromEnd, mirror);

  // Negated: `rotationDegrees` is clockwise, `polyLine2Rotate` counter-clockwise. Negated again
  // under `mirror`, where a clockwise turn in the doc reads counter-clockwise on the mirrored plan.
  return polyLine2RotateDegrees(polyLine2Copy(hostPath), mirror ? rotationDegrees : -rotationDegrees, anchorX, anchorY);
}

/**
 * Where the anchor lands relative to `hostPath`, measured the way `extrudeRectAlongPathExtended`
 * measures it: along the path from the far end when anchored there, and from the far end again when
 * mirrored — so the two cancel — then out perpendicular by `anchorOffset`.
 *
 * `polyLine2EdgeEvalOffset` and `offsetPolyLine2` both take their normal from `line2Normal` with the
 * same orientation, so a negated lateral offset lands on the same side the layers are built on.
 * Wall-hosted props derive their anchor with this same helper and sign — see
 * `propAssetOffsetFromLine2` in `doc/rooms/propPlacement`.
 */
function caseworkAnchorPoint(
  hostPath: PolyLine2,
  anchorParam: number,
  anchorOffset: number,
  paramFromEnd: boolean,
  mirror: boolean,
) {
  const measuredFromPathEnd = paramFromEnd !== mirror;
  const distance = measuredFromPathEnd ? polyLine2Length(hostPath) - anchorParam : anchorParam;

  // Evaluated on the first edge, which is the whole path: rotation is a `FIXED_V2` field, and those
  // placements host on exactly one wall edge.
  const [x, y] = polyLine2EdgeEvalOffset(hostPath, 0, anchorOffset, distance, false);
  return [x, y];
}
